import React, { createContext, useState, useMemo, useEffect, useContext } from 'react';
import {
	CartCountdownTime,
	InactiveAlertTime,
	ExtendTime,
	FinalCheckoutTime,
} from '../pages/common/vhelper/countdownEnums';
import apiService from '../api/apiService';
import { userAuth } from '../contexts/userContext';
import { useNavigate } from 'react-router-dom';

export const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
	const [itemsCount, setItemsCount] = useState(0);
	const [itemsCountstart, setItemsCountstart] = useState(null);
	const [key, setKey] = useState(0);
	const [notiCount, setNotiCount] = useState(0);
	const [notiList, setNotiList] = useState([]);
	const [stage, setStage] = useState('initial');
	const [alertTime, setAlertTime] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	const { token, setToken } = useContext(userAuth);
	const navigate = useNavigate();

	const [isClearing, setIsClearing] = useState(false);

	const fetchTempBookings = async () => {
		if (!token?.session_key) return;

		try {
			setIsLoading(true);
			const item = { action: 'get_temp_bookings' };
			const flechdata = await apiService(item, token, setToken, navigate);

			if (flechdata.type === 'success') {
				let tempItemsCount = 0;
				if (flechdata.bookings) {
					tempItemsCount += flechdata.bookings.length;
				}
				if (flechdata.products) {
					tempItemsCount += flechdata.products.length;
				}

				setItemsCount(tempItemsCount);
				setNotiCount(flechdata.noti_count || 0);

				if (tempItemsCount > 0) {
					if (!itemsCountstart) {
						startCountdown();
					} else if (itemsCountstart && Date.now() > itemsCountstart) {
						handleExpiredCountdown();
					}
				} else {
					clearCountdown();
				}
			}
		} catch (error) {
			console.error('Error fetching temporary bookings:', error);
			setItemsCount(0);
			clearCountdown();
		} finally {
			setIsLoading(false);
		}
	};

	const handleExpiredCountdown = () => {
		if (stage === 'initial') {
			setStage('extend');
			setAlertTime(Date.now() + InactiveAlertTime.ALERT_WAIT * 60000);
		} else if (stage === 'final') {
			setStage('checkout');
			setItemsCountstart(Date.now() + FinalCheckoutTime.CHECKOUT * 60000);
		} else if (stage === 'checkout') {
			clearCart();
		} else if (stage === 'extend' && alertTime && Date.now() > alertTime) {
			clearCart();
		}
	};

	const clearCart = async () => {
		try {
			const formData = { action: 'delete_cart_items' };
			await apiService(formData, token, setToken, navigate);
		} catch (error) {
			console.error('Error clearing cart:', error);
		} finally {
			setIsClearing(false);
			setItemsCount(0);
			clearCountdown();
		}
	};

	const restoreStage = (data) => {
		setItemsCount(data.itemsCount || 0);
		setItemsCountstart(data.itemsCountstart);
		setKey(data.key);
		setNotiCount(data.notiCount || 0);
		setNotiList(data.notiList || []);

		if (data.stage) setStage(data.stage);
		if (data.alertTime) setAlertTime(data.alertTime);

		if (data.itemsCountstart) {
			const isCountdownExpired = Date.now() > data.itemsCountstart;

			if (isCountdownExpired) {
				if (data.stage === 'initial') {
					setStage('extend');
					setAlertTime(Date.now() + InactiveAlertTime.ALERT_WAIT * 60000);
				} else if (data.stage === 'final') {
					setStage('checkout');
					setItemsCountstart(Date.now() + FinalCheckoutTime.CHECKOUT * 60000);
				} else if (stage === 'checkout' && !isClearing) {
					setIsClearing(true);
					clearCart();
				}
			} else if (data.stage === 'extend') {
				setStage('final');
				setAlertTime(null);
			}
		}

		if (data.alertTime && Date.now() > data.alertTime) {
			if (data.stage === 'extend') {
				clearCart();
			}
			setAlertTime(null);
		}
	};

	useEffect(() => {
		const ldata = localStorage.getItem('sdcart_CartContext');

		if (ldata !== null) {
			const data = JSON.parse(ldata);
			restoreStage(data);
		}

		fetchTempBookings();
	}, []);

	useEffect(() => {
		const data = {
			itemsCount,
			itemsCountstart,
			key,
			notiCount,
			notiList,
			stage,
			alertTime,
		};
		localStorage.setItem('sdcart_CartContext', JSON.stringify(data));
	}, [itemsCount, itemsCountstart, key, notiCount, notiList, stage, alertTime]);

	useEffect(() => {
		if (!itemsCountstart && !alertTime) return;

		const interval = setInterval(() => {
			if (itemsCountstart && Date.now() > itemsCountstart) {
				handleExpiredCountdown();
			}

			if (alertTime && Date.now() > alertTime) {
				handleExpiredCountdown();
			}
		}, 1000);

		return () => clearInterval(interval);
	}, [itemsCountstart, alertTime, stage]);

	const startCountdown = () => {
		setItemsCountstart(Date.now() + CartCountdownTime.CART_TIMEOUT * 60000);
		setStage('initial');
		setAlertTime(null);
		setKey((prev) => prev + 1);
	};

	const extendCountdown = () => {
		setItemsCountstart(Date.now() + ExtendTime.EXTEND * 60000);
		setStage('final');
		setAlertTime(null);
		setKey((prev) => prev + 1);
		extendCountdownAPI();
	};

	const extendCountdownAPI = async () => {
		try {
			const formData = { action: 'extend_expire_time' };
			await apiService(formData, token, setToken, navigate);
		} catch (error) {
			console.error('Error extending countdown:', error);
		}
	};

	const startCheckoutCountdown = () => {
		setItemsCountstart(Date.now() + FinalCheckoutTime.CHECKOUT * 60000);
		setStage('checkout');
		setAlertTime(null);
		setKey((prev) => prev + 1);
	};

	const clearCountdown = () => {
		setItemsCountstart(null);
		setAlertTime(null);
		setStage('initial');
		setKey((prev) => prev + 1);
	};

	const addItemToCart = () => {
		setItemsCount((prev) => prev + 1);

		if (itemsCount === 0) {
			startCountdown();
		}
	};

	const removeItemFromCart = () => {
		setItemsCount((prev) => Math.max(0, prev - 1));

		if (itemsCount <= 1) {
			clearCountdown();
		}
	};

	const values = useMemo(
		() => ({
			itemsCount,
			itemsCountstart,
			key,
			notiCount,
			notiList,
			stage,
			alertTime,
			isLoading,

			setItemsCount,
			setItemsCountstart,
			setKey,
			setNotiCount,
			setNotiList,
			setStage,
			setAlertTime,

			startCountdown,
			extendCountdown,
			startCheckoutCountdown,
			clearCountdown,
			addItemToCart,
			removeItemFromCart,
			fetchTempBookings,
			clearCart,
		}),
		[itemsCount, itemsCountstart, key, notiCount, notiList, stage, alertTime, isLoading],
	);

	return <CartContext.Provider value={values}>{children}</CartContext.Provider>;
};
